import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { isChunkLoadError, reloadForNewVersion } from 'lib/chunkErrorRecovery';
import {
  FallbackComponent,
  NewVersionFallbackComponent,
  ProdFallbackComponent,
} from 'components/errorBoundary/FallbackComponent';
import { IS_DEV } from 'config';

/**
 * Router-level error element (DL-64023).
 *
 * A react-router `lazy()` rejection is a ROUTE error, not a render error, so the React
 * <ErrorBoundary> in routes/index.tsx never sees it — without an errorElement react-router
 * renders its own built-in "Unexpected Application Error!" page with a raw stack trace, which
 * is exactly what users reported. Every dynamic import in this app is a route `lazy()`
 * (src/routes/*), so this element is where chunk-load failures surface.
 *
 * Chunk failure -> offer a reload (the build moved on; nothing is broken).
 * Anything else  -> the same fallback the React boundary would have shown, and report it,
 *                   since route errors do not reach that boundary's Sentry integration.
 */
export const RouteError = () => {
  const error = useRouteError();
  const isChunkError = isChunkLoadError(error);

  useEffect(() => {
    if (!isChunkError) Sentry.captureException(error);
  }, [error, isChunkError]);

  if (isChunkError) {
    return <NewVersionFallbackComponent onReload={() => void reloadForNewVersion()} />;
  }

  // Route errors carry no component stack — ComponentStackTrace renders nothing for ''.
  return IS_DEV ? (
    <FallbackComponent
      error={error instanceof Error ? error : new Error(String(error))}
      componentStack=""
      resetError={() => window.location.reload()}
    />
  ) : (
    <ProdFallbackComponent resetError={() => window.location.reload()} />
  );
};
